/**
 * CalendarPageHeader Component
 * 
 * Purpose:
 * Header card component that displays property context (thumbnail, name, subtitle) and controls
 * (property links menu, month selector) for desktop view. Hidden on mobile where property context
 * is shown in the shell header chip. Features scroll-activated sticky positioning that docks
 * below the global nav when scrolled past the sentinel position.
 * 
 * High-level Workflow:
 * 1. Receives property data, UI state, and handlers as props.
 * 2. Conditionally renders only on desktop (!isMobile).
 * 3. Displays property thumbnail (with fallback placeholder) and property name.
 * 4. Property name is clickable to open property overview modal.
 * 5. Shows property links menu and month selector in right section.
 * 6. Applies scroll-activated sticky styling based on isDocked state.
 * 
 * Assumptions & Invariants:
 * - Desktop-only: Component is hidden on mobile (isMobile === true) because property context
 *   is shown in OwnerLayout shell header chip. This prevents duplicate property headers.
 * - Scroll docking: Header card uses scroll-activated sticky positioning. When docked (isDocked
 *   === true), header sticks below global nav with gap. When undocked, header is positioned
 *   relative within hero backdrop area.
 * - Property thumbnail: Uses propertyThumb prop (thumb_url only, curated-only contract).
 *   Falls back to SVG placeholder icon if thumbnail is missing or fails to load.
 * - Property name click: Property name (h1) is clickable when hasProperty is true, opens
 *   property overview modal via setIsPropertyOverviewOpen handler.
 * - Month selector: MonthSelector component receives calendarMonth and setCalendarMonth for
 *   month navigation. Disabled when no property is selected.
 * 
 * Dependencies:
 * - Internal:
 *   - components/owner/PropertyLinksMenu.jsx: Property external links menu
 *   - components/shared/MonthSelector.jsx: Month selection dropdown
 * - External:
 *   - react: None (functional component, no hooks)
 * 
 * Version: 1.0.0
 * 
 * Change Log:
 * - 2026-01-26: Initial extraction from CalendarPage.jsx (Phase 5 refactoring)
 */

import PropertyLinksMenu from './PropertyLinksMenu.jsx';
import MonthSelector from '../shared/MonthSelector.jsx';

/**
 * Calendar page header card component (desktop only)
 * 
 * @param {boolean} isMobile - Whether mobile layout is active (component hidden if true)
 * @param {boolean} isDocked - Whether header is docked (scroll-activated sticky state)
 * @param {boolean} hasProperty - Whether a property is selected
 * @param {boolean} propertiesLoading - Whether properties are loading
 * @param {string} propertyShortName - Property display name
 * @param {string|null} propertyThumb - Property thumbnail URL
 * @param {Object|null} selectedProperty - Selected property object
 * @param {Date} calendarMonth - Current calendar month
 * @param {Function} setCalendarMonth - Handler to change calendar month
 * @param {Function} setIsPropertyOverviewOpen - Handler to open property overview modal
 * @param {Object} styles - Styles object from CalendarPage
 * @param {number[]} [yearOptions] - Data-derived years for Year dropdown (current year first, then descending)
 * @param {boolean} [debug] - When true, add data-debug-year-options on year picker
 * @param {function} [onApply] - Optional (prev, next) callback when Apply is clicked; used for debug logging
 */
export default function CalendarPageHeader({
  isMobile,
  isDocked,
  hasProperty,
  propertiesLoading,
  propertyShortName,
  propertyThumb,
  selectedProperty,
  calendarMonth,
  setCalendarMonth,
  setIsPropertyOverviewOpen,
  styles,
  yearOptions,
  debug = false,
  onApply,
}) {
  // Hidden on mobile (property context is in shell header chip)
  if (isMobile) {
    return null;
  }

  // Desktop: image-column + content-column layout (match OwnerPageChrome header card)
  return (
    <div style={styles.getHeaderCardStyle(isMobile, isDocked)}>
      <div style={styles.getHeaderCardWrapperStyle(isMobile)}>
        {/* Image column: full card height, no padding */}
        <div style={styles.getHeaderImageColumnStyle(isMobile)}>
          {propertyThumb && hasProperty ? (
            <img
              src={propertyThumb}
              alt={`${propertyShortName} thumbnail`}
              style={styles.headerCardImage}
              loading="lazy"
              decoding="async"
              onError={(e) => {
                e.target.style.display = 'none';
                const placeholder = e.target.nextSibling;
                if (placeholder) placeholder.style.display = 'flex';
              }}
            />
          ) : null}
          <div style={{
            ...styles.avatarPlaceholder,
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: (propertyThumb && hasProperty) ? 'none' : 'flex',
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
        {/* Content column: title + controls */}
        <div style={styles.getHeaderContentColumnStyle(isMobile)}>
          <div style={styles.headerLeft}>
            <div style={styles.headerTitleSection}>
              <h1
                style={styles.headerTitle}
                onClick={() => hasProperty && setIsPropertyOverviewOpen(true)}
                onKeyDown={(e) => {
                  if (hasProperty && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    setIsPropertyOverviewOpen(true);
                  }
                }}
                tabIndex={hasProperty ? 0 : -1}
                role={hasProperty ? "button" : undefined}
                aria-label={hasProperty ? "View property overview" : undefined}
              >
                {hasProperty ? propertyShortName : (propertiesLoading ? 'Loading property...' : 'No property selected')}
              </h1>
              <p style={styles.headerSubtitle}>
                Calendar
              </p>
            </div>
          </div>
          <div style={styles.headerRight}>
            {hasProperty && <PropertyLinksMenu property={selectedProperty} />}
            <MonthSelector
              selectedMonth={calendarMonth}
              onMonthChange={setCalendarMonth}
              disabled={!hasProperty}
              yearOptions={yearOptions}
              debug={debug}
              onApply={onApply}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
